import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Plus, Edit, Trash2, Eye, Image, Film, Star, Calendar, User, BookOpen, Save, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Super users who can author blog posts
const SUPER_USERS = [
  { id: 'tony', name: 'Tony Macasaet', role: 'Founder, The Belonging Sessions' },
  { id: 'steve', name: 'Steve Torma', role: 'Facilitator, The REAL Center' },
  { id: 'carrie', name: 'Carrie', role: 'Community Guide' }
];

const CATEGORIES = [
  'Reflections',
  'NVC Foundations',
  'Personal Stories',
  'Teaching Notes',
  'Community Voices',
  'Practice Tips'
];

export default function BlogPostManager({ token }) {
  const [activeTab, setActiveTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    author_id: 'tony',
    category: 'Reflections',
    image_url: '',
    video_url: '',
    featured: false,
    status: 'draft'
  });

  useEffect(() => {
    if (activeTab === 'posts') fetchPosts();
    if (activeTab === 'submissions') fetchSubmissions();
  }, [activeTab]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/blog-posts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Failed to fetch blog posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/blog-submissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions || []);
      }
    } catch (error) {
      console.error('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const author = SUPER_USERS.find(u => u.id === formData.author_id);
    const postData = {
      ...formData,
      author: author?.name || 'Unknown',
      author_role: author?.role || '',
      read_time: `${Math.max(1, Math.ceil(formData.content.split(' ').length / 200))} min read`
    };

    try {
      const url = editingPost 
        ? `${BACKEND_URL}/api/admin/blog-posts/${editingPost._id}`
        : `${BACKEND_URL}/api/admin/blog-posts`;
      
      const response = await fetch(url, {
        method: editingPost ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
      });

      if (!response.ok) throw new Error('Failed to save');

      toast.success(editingPost ? 'Post updated!' : 'Post created!');
      setDialogOpen(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      toast.error('Failed to save post');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/blog-posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to delete');
      
      toast.success('Post deleted');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const toggleFeatured = async (post) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/blog-posts/${post._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...post, featured: !post.featured })
      });
      
      if (!response.ok) throw new Error('Failed');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const approveSubmission = async (submissionId) => {
    if (!window.confirm('Approve this submission and publish it?')) return;
    try {
        const response = await fetch(`${BACKEND_URL}/api/admin/blog-submissions/${submissionId}/approve`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Failed');
        toast.success('Submission approved & published!');
        fetchSubmissions();
    } catch (error) {
        toast.error('Failed to approve');
    }
  };

  const rejectSubmission = async (submissionId) => {
    if (!window.confirm('Reject this submission?')) return;
    try {
        const response = await fetch(`${BACKEND_URL}/api/admin/blog-submissions/${submissionId}/reject`, {
            method: 'PUT',
            headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ note: "Admin rejected" })
        });
        if (!response.ok) throw new Error('Failed');
        toast.success('Submission rejected');
        fetchSubmissions();
    } catch (error) {
        toast.error('Failed to reject');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      author_id: 'tony',
      category: 'Reflections',
      image_url: '',
      video_url: '',
      featured: false,
      status: 'draft'
    });
    setEditingPost(null);
  };

  const openEditDialog = (post) => {
    const authorId = SUPER_USERS.find(u => u.name === post.author)?.id || 'tony';
    setFormData({
      title: post.title || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      author_id: authorId,
      category: post.category || 'Reflections',
      image_url: post.image_url || '',
      video_url: post.video_url || '',
      featured: post.featured || false,
      status: post.status || 'published'
    });
    setEditingPost(post);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
            <span className="text-amber-400">LORE</span> Blog Posts
          </h3>
          <p className="text-stone-400 text-sm">Manage stories and community voices</p>
        </div>
        <div className="flex gap-3">
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
                <Button className="bg-amber-600 hover:bg-amber-500 text-black font-medium">
                <Plus className="w-4 h-4 mr-2" />
                New Post
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-stone-900 border-stone-700 text-white">
                <DialogHeader>
                <DialogTitle className="text-xl text-amber-400" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {editingPost ? 'Edit Blog Post' : 'Create New Blog Post'}
                </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                {/* Title */}
                <div>
                    <Label className="text-stone-400">Title *</Label>
                    <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter a compelling title..."
                    className="mt-1 text-lg bg-stone-800 border-stone-700 text-white"
                    required
                    />
                </div>

                {/* Author & Category Row */}
                <div className="grid md:grid-cols-3 gap-4">
                    <div>
                    <Label className="text-stone-400">Author *</Label>
                    <Select 
                        value={formData.author_id} 
                        onValueChange={(v) => setFormData({ ...formData, author_id: v })}
                    >
                        <SelectTrigger className="mt-1 bg-stone-800 border-stone-700 text-white">
                        <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-stone-800 border-stone-700 text-white">
                        {SUPER_USERS.map(user => (
                            <SelectItem key={user.id} value={user.id}>
                            <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-stone-400" />
                                {user.name}
                            </div>
                            </SelectItem>
                        ))}
                        </SelectContent>
                    </Select>
                    </div>
                    <div>
                    <Label className="text-stone-400">Category *</Label>
                    <Select 
                        value={formData.category} 
                        onValueChange={(v) => setFormData({ ...formData, category: v })}
                    >
                        <SelectTrigger className="mt-1 bg-stone-800 border-stone-700 text-white">
                        <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-stone-800 border-stone-700 text-white">
                        {CATEGORIES.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                        </SelectContent>
                    </Select>
                    </div>
                    <div>
                    <Label className="text-stone-400">Status</Label>
                    <Select 
                        value={formData.status} 
                        onValueChange={(v) => setFormData({ ...formData, status: v })}
                    >
                        <SelectTrigger className="mt-1 bg-stone-800 border-stone-700 text-white">
                        <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-stone-800 border-stone-700 text-white">
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                        </SelectContent>
                    </Select>
                    </div>
                </div>

                {/* Excerpt */}
                <div>
                    <Label className="text-stone-400">Excerpt *</Label>
                    <Textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    placeholder="A brief summary that appears on the blog listing..."
                    className="mt-1 h-20 bg-stone-800 border-stone-700 text-white"
                    required
                    />
                </div>

                {/* Content */}
                <div>
                    <Label className="text-stone-400">Content * (Markdown supported)</Label>
                    <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Write your story here... Use ## for headings, **bold**, *italic*, - for lists"
                    className="mt-1 min-h-[300px] font-mono text-sm bg-stone-800 border-stone-700 text-white"
                    required
                    />
                    <p className="text-xs text-stone-500 mt-1">
                    Tip: Use ## for headings, **text** for bold, *text* for italic, - for bullet points
                    </p>
                </div>

                {/* Media */}
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                    <Label className="text-stone-400 flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        Featured Image URL
                    </Label>
                    <Input
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        placeholder="https://images.unsplash.com/..."
                        className="mt-1 bg-stone-800 border-stone-700 text-white"
                    />
                    </div>
                    <div>
                    <Label className="text-stone-400 flex items-center gap-2">
                        <Film className="w-4 h-4" />
                        Video URL (optional)
                    </Label>
                    <Input
                        value={formData.video_url}
                        onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                        placeholder="https://youtube.com/..."
                        className="mt-1 bg-stone-800 border-stone-700 text-white"
                    />
                    </div>
                </div>

                {/* Featured Toggle */}
                <div className="flex items-center gap-3 p-4 bg-amber-900/20 rounded-lg border border-amber-500/30">
                    <Switch
                    checked={formData.featured}
                    onCheckedChange={(v) => setFormData({ ...formData, featured: v })}
                    />
                    <div>
                    <Label className="text-amber-400 font-medium flex items-center gap-2">
                        <Star className="w-4 h-4" />
                        Featured Post
                    </Label>
                    <p className="text-amber-500/70 text-sm">Featured posts appear prominently at the top of the LORE page</p>
                    </div>
                </div>

                {/* Preview */}
                {formData.image_url && (
                    <div>
                    <Label className="text-stone-400">Image Preview</Label>
                    <div className="mt-2 rounded-lg overflow-hidden border border-stone-700 aspect-video bg-black">
                        <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                        />
                    </div>
                    </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-stone-700">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-stone-600 text-stone-300">
                    Cancel
                    </Button>
                    <Button type="submit" className="bg-amber-600 hover:bg-amber-500 text-black">
                    <Save className="w-4 h-4 mr-2" />
                    {editingPost ? 'Update Post' : 'Create Post'}
                    </Button>
                </div>
                </form>
            </DialogContent>
            </Dialog>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-stone-900 border border-stone-800">
            <TabsTrigger value="posts" className="data-[state=active]:bg-amber-600 data-[state=active]:text-black">Published & Drafts</TabsTrigger>
            <TabsTrigger value="submissions" className="data-[state=active]:bg-amber-600 data-[state=active]:text-black flex items-center gap-2">
                Submissions
                {submissions.filter(s => s.status === 'pending' || s.status === 'pending_review').length > 0 && (
                    <Badge className="bg-red-500 text-white hover:bg-red-600 h-5 w-5 p-0 flex items-center justify-center rounded-full">
                        {submissions.filter(s => s.status === 'pending' || s.status === 'pending_review').length}
                    </Badge>
                )}
            </TabsTrigger>
        </TabsList>

        <TabsContent value="posts">
            <div className="grid gap-4">
                {loading ? (
                <div className="text-center py-12 text-stone-500">Loading posts...</div>
                ) : posts.length === 0 ? (
                <Card className="border-dashed border-2 border-stone-800 bg-stone-900/50">
                    <CardContent className="py-12 text-center">
                    <BookOpen className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-stone-300 mb-2">No Blog Posts Yet</h4>
                    <p className="text-stone-500 mb-4">Create your first LORE story to share with the community</p>
                    <Button onClick={() => setDialogOpen(true)} className="bg-amber-600 hover:bg-amber-500 text-black">
                        <Plus className="w-4 h-4 mr-2" />
                        Create First Post
                    </Button>
                    </CardContent>
                </Card>
                ) : (
                posts.map(post => (
                    <Card key={post._id} className="bg-stone-900 border-stone-800 hover:border-amber-500/30 transition-all">
                    <CardContent className="p-4">
                        <div className="flex gap-4">
                        {/* Thumbnail */}
                        {post.image_url && (
                            <div className="w-32 h-24 rounded-lg overflow-hidden bg-stone-800 flex-shrink-0">
                            <img 
                                src={post.image_url} 
                                alt={post.title}
                                className="w-full h-full object-cover"
                            />
                            </div>
                        )}
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-4">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                {post.featured && (
                                    <Badge className="bg-amber-900/40 text-amber-400 border-amber-500/50">
                                    <Star className="w-3 h-3 mr-1" />
                                    Featured
                                    </Badge>
                                )}
                                <Badge variant="outline" className={post.status === 'published' ? 'border-green-500/50 text-green-400' : 'border-stone-600 text-stone-400'}>
                                    {post.status === 'published' ? 'Published' : 'Draft'}
                                </Badge>
                                <Badge variant="outline" className="border-stone-600 text-stone-400">{post.category}</Badge>
                                </div>
                                <h4 className="text-lg font-medium text-white line-clamp-1">{post.title}</h4>
                                <p className="text-sm text-stone-400 line-clamp-1 mt-1">{post.excerpt}</p>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleFeatured(post)}
                                className={post.featured ? 'text-amber-500 hover:text-amber-400 hover:bg-amber-500/10' : 'text-stone-600 hover:text-stone-400 hover:bg-stone-800'}
                                >
                                <Star className="w-4 h-4" />
                                </Button>
                                <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(post)}
                                className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                                >
                                <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deletePost(post._id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                >
                                <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                            </div>
                            
                            {/* Meta */}
                            <div className="flex items-center gap-4 mt-3 text-xs text-stone-500">
                            <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {post.author}
                            </span>
                            <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(post.created_at).toLocaleDateString()}
                            </span>
                            {post.read_time && <span>{post.read_time}</span>}
                            </div>
                        </div>
                        </div>
                    </CardContent>
                    </Card>
                ))
                )}
            </div>
        </TabsContent>

        <TabsContent value="submissions">
            <div className="grid gap-4">
                {loading ? (
                    <div className="text-center py-12 text-stone-500">Loading submissions...</div>
                ) : submissions.length === 0 ? (
                    <Card className="border-dashed border-2 border-stone-800 bg-stone-900/50">
                        <CardContent className="py-12 text-center">
                            <Clock className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                            <h4 className="text-lg font-medium text-stone-300 mb-2">No Pending Submissions</h4>
                            <p className="text-stone-500">User blog submissions will appear here for review</p>
                        </CardContent>
                    </Card>
                ) : (
                    submissions.map(sub => (
                        <Card key={sub._id} className="bg-stone-900 border-stone-800 hover:border-amber-500/30 transition-all">
                            <CardContent className="p-4">
                                <div className="flex gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-4">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Badge variant="outline" className={`
                                                        ${(sub.status === 'pending' || sub.status === 'pending_review') ? 'border-yellow-500/50 text-yellow-400' : ''}
                                                        ${sub.status === 'approved' ? 'border-green-500/50 text-green-400' : ''}
                                                        ${sub.status === 'rejected' ? 'border-red-500/50 text-red-400' : ''}
                                                    `}>
                                                        {sub.status.toUpperCase()}
                                                    </Badge>
                                                    <Badge variant="outline" className="border-stone-600 text-stone-400">
                                                        {sub.category || 'Community'}
                                                    </Badge>
                                                </div>
                                                <h4 className="text-lg font-medium text-white">{sub.title}</h4>
                                                <p className="text-stone-400 text-sm mt-2 line-clamp-3">{sub.content}</p>
                                            </div>
                                            
                                            <div className="flex flex-col gap-2">
                                                {(sub.status === 'pending' || sub.status === 'pending_review') && (
                                                    <>
                                                        <Button 
                                                            onClick={() => approveSubmission(sub._id)}
                                                            className="bg-green-600 hover:bg-green-500 text-black w-full"
                                                            size="sm"
                                                        >
                                                            <CheckCircle className="w-4 h-4 mr-2" /> Approve
                                                        </Button>
                                                        <Button 
                                                            onClick={() => rejectSubmission(sub._id)}
                                                            variant="outline"
                                                            className="border-red-500/50 text-red-400 hover:bg-red-950/30 w-full"
                                                            size="sm"
                                                        >
                                                            <XCircle className="w-4 h-4 mr-2" /> Reject
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-4 mt-4 text-xs text-stone-500 border-t border-stone-800 pt-3">
                                            <span className="flex items-center gap-1 text-amber-500/70">
                                                <User className="w-3 h-3" />
                                                Submitted by: {sub.author_name} ({sub.author_email})
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {new Date(sub.created_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
